import React from 'react';

/**
 * APEX Design System — Tile (KPI) — v7.4 néon
 *
 * Tuile KPI avec effet néon style Legacy : gradient background, halo radial flou,
 * textShadow sur la valeur, hover scale subtil.
 *
 * Props:
 *   - label  : label uppercase
 *   - value  : valeur principale (string ou number)
 *   - unit   : unité après la valeur (%, g, etc.)
 *   - color  : cyan | rose | amber | violet | emerald | fuchsia | white
 *   - subtext: ligne sous la valeur
 *   - icon   : icône à côté du label
 *   - trend  : 'up' | 'down' | 'flat'
 */

// Configuration néon par couleur sémantique
const NEON_CONFIG = {
  cyan: {
    text: 'text-cyan-400',
    border: 'border-cyan-400/30',
    gradient: 'from-cyan-400/10 via-cyan-400/5 to-transparent',
    halo: '#22d3ee',
    shadow: 'rgba(34, 211, 238, 0.4)'
  },
  rose: {
    text: 'text-rose-400',
    border: 'border-rose-400/30',
    gradient: 'from-rose-500/10 via-rose-500/5 to-transparent',
    halo: '#f43f5e',
    shadow: 'rgba(244, 63, 94, 0.4)'
  },
  amber: {
    text: 'text-amber-400',
    border: 'border-amber-400/30',
    gradient: 'from-amber-400/10 via-amber-400/5 to-transparent',
    halo: '#fbbf24',
    shadow: 'rgba(251, 191, 36, 0.4)'
  },
  violet: {
    text: 'text-violet-400',
    border: 'border-violet-400/30',
    gradient: 'from-violet-400/10 via-violet-400/5 to-transparent',
    halo: '#a78bfa',
    shadow: 'rgba(167, 139, 250, 0.4)'
  },
  emerald: {
    text: 'text-emerald-400',
    border: 'border-emerald-400/30',
    gradient: 'from-emerald-400/10 via-emerald-400/5 to-transparent',
    halo: '#34d399',
    shadow: 'rgba(52, 211, 153, 0.4)'
  },
  fuchsia: {
    text: 'text-fuchsia-400',
    border: 'border-fuchsia-500/30',
    gradient: 'from-fuchsia-500/10 via-fuchsia-500/5 to-transparent',
    halo: '#e879f9',
    shadow: 'rgba(232, 121, 249, 0.4)'
  },
  white: {
    text: 'text-white',
    border: 'border-zinc-800',
    gradient: 'from-zinc-800/30 via-zinc-900/10 to-transparent',
    halo: '#71717a',
    shadow: 'rgba(255, 255, 255, 0.25)'
  }
};

const TREND_ICONS = {
  up: '↗',
  down: '↘',
  flat: '→'
};

const TREND_COLORS = {
  up: 'text-cyan-400',
  down: 'text-rose-400',
  flat: 'text-zinc-500'
};

export default function Tile({
  label,
  value,
  unit,
  color = 'white',
  subtext,
  icon: Icon,
  trend,
  className = ''
}) {
  const config = NEON_CONFIG[color] || NEON_CONFIG.white;

  return (
    <div
      className={`
        relative border ${config.border}
        bg-gradient-to-br ${config.gradient}
        p-5 overflow-hidden
        group hover:scale-[1.02] transition-transform
        ${className}
      `}
    >
      {/* Halo radial flou en haut-droite (effet néon principal) */}
      <div
        className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-2xl pointer-events-none"
        style={{ background: config.halo }}
        aria-hidden="true"
      />

      <div className="relative">
        {/* Header : label + icon + trend */}
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {Icon && <Icon size={13} className="text-zinc-600 shrink-0" />}
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 font-bold truncate">
              {label}
            </div>
          </div>
          {trend && (
            <span className={`text-base ${TREND_COLORS[trend]} font-mono shrink-0`}>
              {TREND_ICONS[trend]}
            </span>
          )}
        </div>

        {/* Valeur HÉROE avec textShadow néon */}
        <div className="flex items-baseline gap-1.5">
          <span
            className={`text-4xl font-black ${config.text} leading-none tracking-tight`}
            style={{ textShadow: `0 0 20px ${config.shadow}` }}
          >
            {value}
          </span>
          {unit && (
            <span className={`text-base ${config.text} opacity-70 font-mono font-bold`}>
              {unit}
            </span>
          )}
        </div>

        {/* Subtext */}
        {subtext && (
          <div className="text-[10px] text-zinc-500 font-mono mt-2 truncate">
            {subtext}
          </div>
        )}
      </div>
    </div>
  );
}
